import {
  calculateTaxByZipcode,
  checkTollShippingAvailability,
  updateEstimatedDate,
  updateOrderOmniChannelShippingInfo,
  updateOrderShippingInfo,
} from '@api/checkout'
import {
  ApiError,
  DeliveryEstimation, EmptyLocation,
  EstimationData, ExpressShippingEstimationResponse, FormattedAddress, FormattedStore, LocationDetail,
  OmniChannelShippingInfoPayload, Order, OrderItem, OverlayFlowStatus,
  SelectedStore, ShippingMethodsResponse,
  StoreDetails, TollData, TollShippingAvailabilityPayload,
} from '@interfaces/checkout'
import first from 'lodash/first'
import isArray from 'lodash/isArray'
import { safeJSONParse } from '@libs/utils/string'
import isString from 'lodash/isString'
import isEmpty from 'lodash/isEmpty'
import {
  CEP_DATE_STORAGE_KEY,
  CEP_SLOT_STORAGE_KEY,
  CEP_ZIP_STORAGE_KEY,
  CNC_ADDRESS_STRING_STORAGE_KEY,
  CNC_SHIPPING_DATA_KEY,
  CNC_STORES_API_DATA_KEY,
  CNC_STORES_WEEK_DAYS, GRAVITY_SHIPPING_TYPE, omniChannelErrors,
  SGH_CART_ITEMS_KEY,
  SHIPPING_OVERLAY_FLOW,
  ShippingMethodsKeys,
  STS_ADDRESS_STRING_STORAGE_KEY, STS_DELIVERY_LOCAL_DATA_KEY, STS_SHIPPING_DATA_KEY,
  STS_SHIPPING_TYPE, STS_SHIPPING_WINDOW_ID, STS_SHIPPING_WINDOW_KEY,
  TOLL_DATA_ADDRESS_KEY,
  TOLL_DATA_FULL_ADDRESS_KEY, TOLL_SHIPPING_ADDRESS_KEY,
} from '@libs/utils/const'
import {
  formatStoresList,
  is24HourFormat,
  prioritizeStoreById,
  reshuffleStoresList,
  sortStores,
} from '@libs/utils/stores'
import { CNC_OVERLAY_STEP } from '@libs/utils/cnc-const'
import wcs from '@libs/wcs'
import { getNumberitem } from '@libs/wcs/minicart'


/**
 * Updates the shipping mode for all items in the cart.
 *
 * @async
 * @function updateShipModeForAllItems
 * @param {Object} params - The parameters for updating the shipping mode.
 * @param {string} params.shippingMethodId - The ID of the selected shipping method.
 * @param {string} params.shippingMethodName - The name of the selected shipping method.
 * @param {string} [params.storeId] - The ID of the store (optional).
 * @param {string} [params.catalogId] - The ID of the catalog (optional).
 * @param {string} [params.langId] - The language ID (optional).
 * @param {string} [params.orderId] - The ID of the order (optional).
 * @param {SelectedStore | null} [params.selectedCncStore] - The selected CNC store details (optional).
 * @param {DeliveryEstimation | null} [params.currentSFSDeliveryEstimation] - The current SFS delivery estimation (optional).
 * @param {EstimationData | null} [params.estimationData] - The estimation data for shipping window (optional).
 * @param {Record<string, any>} [params.orderItemsData] - The order items data (optional).
 * @param {Object} [params.tollData] - The toll shipping data (optional).
 *
 * @returns {Promise<void>} Resolves when the shipping mode is successfully updated.
 *
 * @throws {Error} Logs an error if the update process fails.
 *
 * @description
 * This function handles updating the shipping mode for all items in the cart. It performs the following:
 * - Disables buttons during the update process.
 * - Handles specific shipping methods like "express_sd_TOLL" and "CNC".
 * - Updates the shipping information via API calls.
 * - Handles OmniChannel shipping if applicable.
 *
 * @example
 * // Example usage:
 * await updateShipModeForAllItems({
 *   shippingMethodId: '123',
 *   shippingMethodName: 'express_sd_TOLL',
 *   storeId: '456',
 *   catalogId: '789',
 *   langId: 'en',
 *   storeData: { orderId: '001', orderType: 'CNC', physicalStoreId: 'store123' },
 * });
 */
export const updateShipModeForAllItems = async ({
  shippingMethodId,
  shippingMethodName,
  shippingMethodType,
  countryCode = '',
  storeId = '',
  catalogId = '',
  langId = '',
  orderId = '',
  selectedCncStore = null,
  currentSFSDeliveryEstimation = null,
  estimationData = null,
  orderItemsData = {},
  tollData = undefined,
  tollSelectedStore = null,
}: {
  shippingMethodId: string
  shippingMethodName: string
  shippingMethodType: string
  countryCode: string
  storeId?: string
  catalogId?: string
  orderId?: string
  langId?: string
  selectedCncStore?: SelectedStore | null
  estimationData?: EstimationData | null
  currentSFSDeliveryEstimation?: DeliveryEstimation | null
  orderItemsData?: Record<string, any>
  tollData? : TollData | undefined
  tollSelectedStore?: SelectedStore | null
}): Promise<void> => {
  try {
    if (!shippingMethodId || shippingMethodId === '-1') return
    const { addressString:tollDataAddressString = '', zip = '', latitude, longitude } = tollData || {}
    disableButtons()

    await updateOrderShippingInfo({
      storeId: storeId || '',
      catalogId: catalogId || '',
      langId: langId || '',
      shipModeId: shippingMethodId,
    }).catch(console.error)

    if ((shippingMethodType === STS_SHIPPING_TYPE || shippingMethodType === GRAVITY_SHIPPING_TYPE) && countryCode.toLowerCase() === 'au' && !isEmpty(tollDataAddressString)) {
      const tollAvailabilityRes = await updateTollShippingAvailability({
        langId: Number(langId),
        orderId,
        storeId: Number(storeId),
        userAddressString:tollDataAddressString || '',
        zipcode: zip || '',
        latitude:Number(latitude),
        longitude: Number(longitude),
        orderItemsData,
      }).catch(error => {
        console.error('Failed to get toll shipping availability:', error)
      })
      !!tollAvailabilityRes && await updateOrderOmniChannelShippingInfo({
        orderId:orderId,
        physicalStoreId:tollAvailabilityRes?.physicalStoreId,
        orderType:tollAvailabilityRes?.orderType,
      }).catch(err => {
        console.error('Error updating order omni channel shipping info for toll express ', err)
        throw new Error('Failed to update order omni channel shipping info for toll express')
      })
    }
    if (shippingMethodType?.toLowerCase() === 'cnc' && !isEmpty(selectedCncStore)) {
      const {
        orderType = '',
        physicalStoreId = '',
        physicalStoreZipcode = '',
      } = selectedCncStore || {}
      const cncUpdateRes = await updateOrderOmniChannelShippingInfo({
        storeId,
        catalogId,
        langId,
        orderId,
        physicalStoreId,
        physicalStoreZipcode,
        orderType,
      }).catch(console.error)
      if (!isEmpty(cncUpdateRes)) {
        localStorage.setItem(
          CNC_SHIPPING_DATA_KEY,
          JSON.stringify(parseOmniChannelShippingApiResponse(cncUpdateRes?.data || {}))
        )
      }
    }
    await updateEstimatedDate({
      storeId,
      langId,
      catalogId,
      orderId,
    }).catch(error => {
      console.error('Failed to update estimated date:', error)
    })
    // If one-day shipping is selected, set the shipping window with the current estimation data
    if (
      isOneDayShipping(shippingMethodName) &&
      !isEmpty(estimationData) &&
      !isEmpty(currentSFSDeliveryEstimation)
    ) {
      await setShippingWindow({
        estimationData,
        currentDeliveryEstimation: currentSFSDeliveryEstimation,
        orderId: orderId,
        storeId: storeId,
      }).catch(error => {
        console.error('Failed to set shipping window:', error)
      })
    }

    //update view
    await updateContextPromise('ShopCartPaginationDisplay_Context', {
      appliedPromo: '',
      errorMessage: '',
      errorKey: '',
      promotionView: '',
    })
  } catch (e) {
    console.error('error updating ship mode for all items:', e)
  }
}

const disableButtons = () => {
  document.getElementById('total-bottom')?.classList.add('buttons-disabled')
}

/** Handles the display of out-of-stock messages for Stella items in the cart.**/
export const handleStellaItems = (message: string) => {
  const stellaItemElement = document.querySelector('[data-stella-item]')
  const stellaItemId = stellaItemElement
    ? stellaItemElement.closest('[data-orderitemid]')?.getAttribute('data-orderitemid')
    : null
  if (stellaItemId) {
    const targetBlock = document.querySelector(`[data-orderitemid="${stellaItemId}"]`)
    if (targetBlock && !targetBlock.classList.contains('low-stock')) {
      targetBlock.classList.add('low-stock')
      const errorRow = `
      <tr class="error error--quantity">
        <td colspan="5">
          <p class="out-of-stock">${message}</p>
        </td>
      </tr>`
      targetBlock.insertAdjacentHTML('beforebegin', errorRow)
    }
  }
}

/**
 * Parses the OmniChannel shipping API response and filters out keys with null or empty values.
 *
 * @function parseOmniChannelShippingApiResponse
 * @param {Object} response - The API response object to parse.
 * @param {Object.<string, any>} response - A key-value pair object containing the API response data.
 * @returns {OmniChannelShippingInfoPayload} The parsed and filtered OmniChannel shipping information payload.
 *
 * @throws {Error} Logs an error if the parsing process fails.
 *
 * @description
 * This function processes the API response by:
 * - Mapping over the response entries to handle array values (extracting the first element if the value is an array).
 * - Filtering out keys with null or empty string values.
 * - Returning the filtered object as an `OmniChannelShippingInfoPayload`.
 *
 * @example
 * // Example usage:
 * const response = {
 *   orderType: 'CNC',
 *   orderId: ['12345'],
 *   localWindowData: null,
 *   pickUpReadyDate: '',
 *   storeId: ['store123'],
 * };
 * const parsedResponse = parseOmniChannelShippingApiResponse(response);
 * console.log(parsedResponse);
 * // Output: { orderType: 'CNC', orderId: '12345', storeId: 'store123' }
 */
export const parseOmniChannelShippingApiResponse = (response: {
  [key: string]: any
}): OmniChannelShippingInfoPayload => {
  try {
    const parsedStoreData = Object.fromEntries(
      Object.entries(response)?.map(([key, value]) => [key, isArray(value) ? first(value) : value])
    )

    // Filter out keys with null or empty values
    const filteredStoreData: OmniChannelShippingInfoPayload = {
      orderType: parsedStoreData.orderType || null,
      orderId: parsedStoreData.orderId || '',
      localWindowData: parsedStoreData.localWindowData || '',
      pickUpReadyDate: parsedStoreData.pickUpReadyDate || null,
      endDateTime: parsedStoreData.endDateTime || null,
      storeId: parsedStoreData.storeId || '',
      destinationId: parsedStoreData.destinationId || null,
      windowId: parsedStoreData.windowId || null,
      localWindowText: parsedStoreData.localWindowText || '',
      senderId: parsedStoreData.senderId || null,
      startDateTime: parsedStoreData.startDateTime || null,
      expiresDateTime: parsedStoreData.expiresDateTime || null,
      physicalStoreId: parsedStoreData.physicalStoreId || '',
      physicalStoreZipcode: parsedStoreData.physicalStoreZipcode || '',
    }

    return Object.fromEntries(
      Object.entries(filteredStoreData).filter(([_, value]) => value !== null && value !== '')
    ) as OmniChannelShippingInfoPayload
  } catch (e) {
    console.error('error parsing omni channel shipping api response:', e)
    return response
  }
}

/**
 * @file Utility for flattening JSON-like objects:
 *       if a value is an array containing only one element,
 *       it replaces the array with that single value.
 */

/**
 * Returns a shallow copy of an object where all single-element arrays
 * are replaced by their contained value.
 *
 * @template T - The type of the input object.
 * @param {T} obj - The object to process.
 * @returns {T} A new version of the object with single-element arrays flattened.
 *
 * @example
 * ```ts
 * const input = { a: ["1"], b: [1, 2], c: "x" };
 * const result = flattenSingleElementArrays(input);
 * // result => { a: "1", b: [1, 2], c: "x" }
 * ```
 */
export function flattenSingleElementArrays<T extends Record<string, any>>(obj: T): T {
  const result: Record<string, any> = {}

  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value) && value.length === 1) {
      result[key] = value[0]
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Recursively handle nested objects
      result[key] = flattenSingleElementArrays(value)
    } else {
      result[key] = value
    }
  }

  return result as T
}

/** Parses a JSON string that may contain comments and escaped characters.**/
export const parseCommentedJson = (rawStr: string) => {
  try {
    let cleaned = rawStr.replace(/\/\*|\*\//g, '')
    cleaned = cleaned.trim()
    cleaned = cleaned.replace(/\\n/g, '\n').replace(/\\t/g, '\t')
    return safeJSONParse(cleaned)
  } catch (e) {
    console.error('error parsing commented json:', e)
    return {}
  }
}

/**
 * Updates the context for a given key and value using a promise-based approach.
 *
 * @function updateContextPromise
 * @param {string} contextKey - The key of the context to update.
 * @param {any} contextValue - The value to set for the specified context key.
 * @returns {Promise<void>} A promise that resolves when the context is successfully updated.
 *
 * @throws {Error} Rejects the promise if an error occurs during the update process.
 *
 * @example
 * // Example usage:
 * updateContextPromise('ShopCartPaginationDisplay_Context', {
 *   appliedPromo: '',
 *   errorMessage: '',
 *   errorKey: '',
 *   promotionView: '',
 * }).then(() => {
 *   console.log('Context updated successfully');
 * }).catch((error) => {
 *   console.error('Error updating context:', error);
 * });
 */
export const updateContextPromise = (contextKey: string, contextValue: any): Promise<void> => {
  return new Promise((resolve, reject) => {
    const onCartUpdated = () => {
      try {
        document.removeEventListener('CartUpdated', onCartUpdated) // remove the listener
        resolve()
      } catch (error) {
        reject(error)
      }
    }

    try {
      window.wc.render?.updateContext(contextKey, contextValue)
      document.addEventListener('CartUpdated', onCartUpdated) // add listener
    } catch (error) {
      document.removeEventListener('CartUpdated', onCartUpdated) // remove listener in case of error
      reject(error)
    }
  })
}

/**
 * Sets the shipping window for an order by updating the OmniChannel shipping information.
 *
 * @async
 * @function setShippingWindow
 * @param {Object} params - The parameters for setting the shipping window.
 * @param {DeliveryEstimation | null} params.currentDeliveryEstimation - The delivery estimation details, including window and time information.
 * @param {EstimationData | null} params.estimationData - Additional estimation data for the shipping window.
 * @param {string} params.estimationData.- The ID of the physical store.
 * @param {string} params.estimationData.physicalStoreZipcode - The zipcode of the physical store.
 * @param {string} params.estimationData.orderType - The type of the order (e.g., CNC).
 * @param {string} params.estimationData.pickUpReadyDate - The date when the order is ready for pickup.
 * @param {string} params.orderId - The ID of the order.
 * @param {string} params.storeId - The ID of the store.
 * @returns {Promise<OmniChannelShippingInfoPayload | ApiError | null>} Resolves with the parsed OmniChannel shipping information payload, an API error, or `null` if an error occurs.
 *
 * @throws {Error} Logs an error if the update process fails.
 *
 * @description
 * This function updates the shipping window for an order by:
 * - Sending a request to update the OmniChannel shipping information.
 * - Parsing the response to extract relevant data or error messages.
 * - Returning the parsed response or `null` in case of failure.
 *
 * @example
 * // Example usage:
 * const deliveryEstimation = {
 *   windowId: '123',
 *   startDateTime: '2023-10-01T10:00:00Z',
 *   endDateTime: '2023-10-01T12:00:00Z',
 *   expiresDateTime: '2023-10-01T15:00:00Z',
 *   dropdownOptionLabel: '10:00 AM - 12:00 PM',
 *   senderId: 'sender123',
 *   destinationId: 'destination123',
 * };
 * const estimationData = {
 *   physicalStoreId: 'store123',
 *   physicalStoreZipcode: '12345',
 *   orderType: 'CNC',
 *   pickUpReadyDate: '2023-10-01',
 * };
 * const result = await setShippingWindow({
 *   deliveryEstimation,
 *   estimationData,
 *   orderId: 'order123',
 *   storeId: 'store456',
 * });
 * console.log(result);
 */
export const setShippingWindow = async ({
  currentDeliveryEstimation,
  estimationData,
  orderId,
  storeId,
}: {
  currentDeliveryEstimation: DeliveryEstimation | null
  estimationData: EstimationData | null
  orderId: string
  storeId: string
}): Promise<OmniChannelShippingInfoPayload | ApiError | null> => {
  try {
    const {
      physicalStoreId = '',
      orderType = '',
      pickUpReadyDate = '',
      physicalStoreZipcode = '',
    } = estimationData || {}
    const updatedStsShippingInfoRes = await updateOrderOmniChannelShippingInfo({
      orderId: orderId,
      physicalStoreId,
      orderType,
      pickUpReadyDate,
      windowId: currentDeliveryEstimation?.windowId,
      startDateTime: currentDeliveryEstimation?.startDateTime,
      endDateTime: currentDeliveryEstimation?.endDateTime,
      expiresDateTime: currentDeliveryEstimation?.expiresDateTime,
      localWindowText: currentDeliveryEstimation?.dropdownOptionLabel,
      localWindowData: JSON.stringify(currentDeliveryEstimation),
      physicalStoreZipcode,
      storeId,
      senderId: currentDeliveryEstimation?.senderId,
      destinationId: currentDeliveryEstimation?.destinationId,
    })
    //parse commented json response to find error messages if any
    return isString(updatedStsShippingInfoRes?.data)
      ? parseCommentedJson(updatedStsShippingInfoRes?.data || '{}')
      : parseOmniChannelShippingApiResponse(updatedStsShippingInfoRes?.data || {})
  } catch (e) {
    return null
  }
}

export const updateContexts = async () => {
  try {
     await updateContextPromise('ShopCartPaginationDisplay_Context', {
      appliedPromo: '',
      errorMessage: '',
      errorKey: '',
      promotionView: '',
    })
  } catch (error) {
    console.error('Error updating contexts:', error)
  }
}
export const updateShippingInfo = async (payload: {
  orderType?: string
  physicalStoreId?: string
  physicalStoreZipcode?: string
  storeId?: string
  langId?: string
  catalogId?: string
  orderId?: string
}) => {
  const {
    storeId = '',
    langId = '',
    catalogId = '',
    orderId = '',
    orderType = '',
    physicalStoreId = '',
    physicalStoreZipcode = '',
  } = payload || {}

  const cncUpdateRes = await updateOrderOmniChannelShippingInfo({
    storeId,
    catalogId,
    langId,
    orderId,
    physicalStoreId,
    physicalStoreZipcode,
    orderType,
  }).catch(console.error)
  return parseOmniChannelShippingApiResponse(cncUpdateRes?.data || {})
}

/**
 * Stores CNC-related data into localStorage.
 * @param {string} shippingDataKey - The key for storing shipping data.
 * @param {any} shippingData - The shipping data to store.
 * @param {string} storesDataKey - The key for storing stores data.
 * @param {StoreDetails | null} storesData - The stores data to store.
 * @param {string} addressKey - The key for storing the selected address.
 * @param {string} selectedAddress - The selected address to store.
 */
export function storeCncData(
  {
    shippingDataKey = '',
    shippingData = null,
    storesDataKey = '',
    storesData = null,
    addressKey = '',
    selectedAddress = '',
  } :{
    shippingDataKey?: string,
    shippingData?: any,
    storesDataKey?: string,
    storesData?: StoreDetails | null,
    addressKey?: string,
    selectedAddress?: string,
  }
): void {
  !!shippingDataKey && localStorage.setItem(shippingDataKey, JSON.stringify(shippingData))
  !!storesDataKey && localStorage.setItem(storesDataKey, JSON.stringify(storesData))
  !!addressKey && localStorage.setItem(addressKey, selectedAddress || '')
}

/**
 * Updates the state and local storage for CNC (Click and Collect) shipping method.
 *
 * @async
 * @function updateStateAndLocalStorageForCnc
 * @param {Object} params - The parameters for the function.
 * @param {string} params.storeId - The ID of the store.
 * @param {string} params.catalogId - The ID of the catalog.
 * @param {string} params.langId - The language ID.
 * @param {string} params.orderId - The ID of the order.
 * @param {Object} params.selectedCncStore - The selected CNC store details.
 * @param {string} params.selectedCncStore.physicalStoreId - The physical store ID.
 * @param {string} params.selectedCncStore.physicalStoreZipcode - The physical store's zipcode.
 * @param {string} params.selectedCncStore.orderType - The order type (default: 'CNC').
 * @param {Object} params.storesCncApiData - The CNC stores API data.
 * @param {string} params.lastSearchedAddressForCnc - The last searched address for CNC.
 * @returns {Promise<void>} Resolves when the state and local storage are successfully updated.
 *
 * @throws {Error} Logs an error if the update process fails.
 *
 * @description
 * This utility function updates the CNC shipping information by:
 * - Sending an API request to update the shipping info.
 * - Storing the updated data in local storage.
 * - Updating the application context.
 *
 * @example
 * await updateStateAndLocalStorageForCnc({
 *   storeId: '123',
 *   catalogId: '456',
 *   langId: 'en',
 *   orderId: '789',
 *   selectedCncStore: {
 *     physicalStoreId: 'store123',
 *     physicalStoreZipcode: '12345',
 *     orderType: 'CNC',
 *   },
 *   storesCncApiData: {},
 *   lastSearchedAddressForCnc: '123 Main St',
 * });
 */
export const updateStateAndLocalStorageForCnc = async ({
 storeId = '',
 catalogId = '',
 langId = '',
 orderId = '',
 selectedCncStore = {},
 storesCncApiData = null,
 lastSearchedAddressForCnc = '',
}: {
  storeId: string;
  catalogId: string;
  langId: string;
  orderId: string;
  selectedCncStore: SelectedStore;
  storesCncApiData: StoreDetails | null;
  lastSearchedAddressForCnc: string;
}): Promise<void> => {
  try {
    const updatedShippingInfoRes = await updateShippingInfo({
      storeId,
      catalogId,
      langId,
      orderId,
      physicalStoreId: selectedCncStore?.physicalStoreId || '',
      physicalStoreZipcode: selectedCncStore?.physicalStoreZipcode || '',
      orderType: selectedCncStore?.orderType || 'CNC',
    }).catch(console.error);
    storeCncData({
      shippingDataKey:CNC_SHIPPING_DATA_KEY,
      shippingData: parseOmniChannelShippingApiResponse(updatedShippingInfoRes || {}),
      storesDataKey:CNC_STORES_API_DATA_KEY,
      storesData: storesCncApiData,
      addressKey:CNC_ADDRESS_STRING_STORAGE_KEY,
      selectedAddress: lastSearchedAddressForCnc,
    }
    );

    await updateContexts();
  } catch (error) {
    console.error('Error in updateStateAndLocalStorageForCnc:', error);
  }
};

/**
 * Fetches toll shipping availability for a given address and updates the order's OmniChannel shipping info.
 *
 * @async
 * @function updateTollShippingAvailability
 * @param {Object} params - The parameters for the function.
 * @param {string} params.userAddressString - The formatted address string.
 * @param {string} params.zipcode - The postal code of the selected address.
 * @param {number} params.latitude - The latitude of the selected address.
 * @param {number} params.longitude - The longitude of the selected address.
 * @param {string} params.orderId - The ID of the order.
 * @param {string} params.langId - The language ID.
 * @param {Object} params.orderItemsData - The order items data object.
 * @param {string} params.storeId - The store ID.
 * @returns {Promise<Object>} Resolves with toll shipping data or throws an error.
 *
 * @throws {Error} Throws an error if the API call fails or if there are validation issues.
 */
export async function updateTollShippingAvailability({
  userAddressString,
  zipcode,
  latitude,
  longitude,
  orderId,
  langId,
  orderItemsData,
  storeId,
}: {
  userAddressString: string
  zipcode: string
  latitude: number
  longitude: number
  orderId: string
  langId: number
  orderItemsData: Record<string, any>
  storeId: number
}): Promise<{
  tollData: { latitude: number; longitude: number; zip: string }
  userAddressString: string,
  physicalStoreId: string,
  orderType: string
}> {
  try {
    const tollAvailabilityPayload: TollShippingAvailabilityPayload = {
      latitude,
      longitude,
      orderId,
      langId,
      'partnumbers[]': Object.keys(orderItemsData || {}),
      storeId,
      zipcode,
    }

    const tollAvailabilityRes = await checkTollShippingAvailability(tollAvailabilityPayload).catch(
      err => {
        console.error('Error checking toll shipping availability', err)
        throw new Error('Failed to check toll shipping availability')
      }
    )


    const parsedResponse = isString(tollAvailabilityRes?.data)
      ? parseCommentedJson(tollAvailabilityRes?.data)
      : tollAvailabilityRes?.data
    if (!isEmpty(parsedResponse?.errorMessageKey)) {
      throw new Error(parsedResponse?.errorMessageKey)
    }
    const flattenResponse = flattenSingleElementArrays(parsedResponse)

    const {
      latitude: responseLatitude,
      longitude: responseLongitude,
      zipcode: responseZipCode,
      physicalStoreId = '',
      orderType = '',
    } = flattenResponse || {}
    const tollData = {
      latitude: Number(responseLatitude),
      longitude: Number(responseLongitude),
      zip: responseZipCode,
    }

    return { tollData, userAddressString,physicalStoreId, orderType }
  } catch (error) {
    console.error('Error updating toll shipping availability:', error)
    throw error
  }
}


/**
 * Updates the shipping window and calculates taxes based on the provided delivery estimation.
 *
 * @async
 * @function setShippingWindow
 * @param {Object} params - The parameters for the function.
 * @param {DeliveryEstimation} params.currentDeliveryEstimation - The current delivery estimation.
 * @param {string} params.orderId - The ID of the order.
 * @param {string} params.langId - The language ID.
 * @param {string} params.catalogId - The catalog ID.
 * @param {string} params.storeId - The store ID.
 * @param {Object} params.estimationData - The estimation data containing physical store details.
 * @returns {Promise<void>} Resolves when the operation is complete.
 *
 * @throws {Error} Throws an error if updating the shipping window or calculating taxes fails.
 */
export async function updateShippingWindow({
 currentDeliveryEstimation,
 orderId,
 langId,
 catalogId,
 storeId,
 estimationData,
}: {
  currentDeliveryEstimation: DeliveryEstimation | null;
  orderId: string;
  langId: string;
  catalogId: string;
  storeId: string;
  estimationData: EstimationData | null;
}): Promise<void> {
  try {
    const parsedUpdatedShippingInfoRes = !!currentDeliveryEstimation
      ? await setShippingWindow({
        estimationData,
        currentDeliveryEstimation,
        orderId,
        storeId,
      }).catch(error => {
        console.error('Failed to set shipping window:', error)
      })
      : null
    if (!isEmpty(parsedUpdatedShippingInfoRes?.errorMessageKey)) {
      throw new Error(parsedUpdatedShippingInfoRes?.errorMessageKey);
    }

    // Update local storage for legacy checkout page

    storeStsData({
      shippingWindow: parsedUpdatedShippingInfoRes?.localWindowText || '',
      localWindowData: parsedUpdatedShippingInfoRes?.localWindowData || '',
    })

    // Calculate tax by zipcode
    await calculateTaxByZipcode({
      orderId,
      langId,
      catalogId,
      zipcode: estimationData?.physicalStoreZipcode || '',
      storeId,
    }).catch((error) => {
      console.error('Error calculating tax by zipcode for same day delivery:', error);
      throw new Error('Failed to calculate tax by zipcode');
    });

  } catch (error) {
    console.error('Error in setShippingWindow:', error);
    throw error;
  }
}

export function storeStsData(
  {
    estimationData = null,
    shippingWindow = '',
    selectedAddress = '',
    localWindowData = null,
  } :{
    estimationData?: any,
    shippingWindow?: string | null,
    selectedAddress?: string | null,
    localWindowData?: string | null,
  }
): void {
  if(!!shippingWindow) {
    console.log('Storing shipping window in localStorage:', shippingWindow)
    localStorage.setItem(STS_SHIPPING_WINDOW_KEY,shippingWindow)
    localStorage.setItem(STS_SHIPPING_WINDOW_ID,shippingWindow)
  }
  if(!!estimationData) {
    localStorage.setItem(STS_DELIVERY_LOCAL_DATA_KEY, JSON.stringify(estimationData))
  }
  if(!!selectedAddress){
    localStorage.setItem(STS_ADDRESS_STRING_STORAGE_KEY, selectedAddress)
  }
  if(!!localWindowData){
    localStorage.setItem(STS_SHIPPING_DATA_KEY, localWindowData)
  }

}
export const isOneDayShipping = (shippingMethodName: string): boolean => {
  return ['express_sd Roadie', 'express_sd_99Minutos'].includes(shippingMethodName)
}
export const isTollShipping = (shippingMethodType: string): boolean => {
  return ['express_sd'].includes(shippingMethodType)
}
/*  Type guard to check if an object is of type ApiError */
export const isApiError = (obj: any): obj is ApiError => {
  return obj && 'errorMessageKey' in obj;
}

// Type guard per ExpressShippingEstimationResponse
export const isExpressShippingEstimationResponse = (
  obj: any
): obj is ExpressShippingEstimationResponse =>{
  return obj && 'physicalStoreUTCOffset' in obj;
}



// Helper functions


/*
* initialize the Click&Collect state
* */
export const getInitialCNCState = () => {
  try {
    const storesApiData = safeJSONParse(window.localStorage.getItem(CNC_STORES_API_DATA_KEY) || '{}')
    const selectedStoreData = safeJSONParse(window.localStorage.getItem(CNC_SHIPPING_DATA_KEY) || '{}')
    const addressString = window.localStorage.getItem(CNC_ADDRESS_STRING_STORAGE_KEY) || ''
    const orderItemsData = safeJSONParse(window.localStorage.getItem('orderData') || '{}')
    const sortedStoresByInventory = sortStores(storesApiData,orderItemsData)
    const overlayFlowStatus = safeJSONParse(window.localStorage.getItem(SHIPPING_OVERLAY_FLOW) || '{}')
    const formattedStores =  !isEmpty(storesApiData) ? formatStoresList(
      sortedStoresByInventory,
      CNC_STORES_WEEK_DAYS,
      orderItemsData,
      !is24HourFormat()
    ) : []
    const preselectedStoreFromPdp = () => {
      try {
        if (!isEmpty(selectedStoreData)) {
          return selectedStoreData
        } else {
          return null
        }
      } catch (e) {
        return null
      }
    }
    return {
      storesApiData: isEmpty(storesApiData) ? null : storesApiData,
      orderedStoreIds: sortedStoresByInventory.map(store => store.id),
      storesFormattedList: isEmpty(formattedStores) ? null : reshuffleStoresList(formattedStores,selectedStoreData),
      selectedStore: isEmpty(selectedStoreData) ? null : {
        ...parseOmniChannelShippingApiResponse(selectedStoreData),
        physicalStoreZipcode: selectedStoreData.physicalStoreZipcode
      },
      selectedAddress: isEmpty(addressString) ? null : { addressString },
      overlaySteps: [
        { name: CNC_OVERLAY_STEP.addressSelection, active: false },
        { name: CNC_OVERLAY_STEP.storeSelection, active: false },
        { name: CNC_OVERLAY_STEP.review, active: false }
      ],
      overlayFlowStatus: !isEmpty(preselectedStoreFromPdp()) ? 'complete':overlayFlowStatus?.CNC || 'empty',
      confirmationClicked: false,
    }
  } catch (error) {
    console.error('Error initializing CNC state:', error)
    return {
      storesApiData: null,
      orderedStoreIds: [],
      storesFormattedList: null,
      selectedStore: null,
      selectedAddress: null,
      overlaySteps: [],
      overlayFlowStatus: null,
      confirmationClicked: false,
    }
  }
}

export const getInitialSTSState = () => {
  try {
    const overlayFlowStatus = safeJSONParse(window.localStorage.getItem(SHIPPING_OVERLAY_FLOW) || '{}')
    const sfsData:ExpressShippingEstimationResponse = safeJSONParse(
      window.localStorage.getItem(STS_DELIVERY_LOCAL_DATA_KEY) || '{}'
    )
    if (isEmpty(sfsData)) {
      return {
        selectedSfsdAddress:  {
          addressString: window.localStorage.getItem(STS_ADDRESS_STRING_STORAGE_KEY) || '',
        },
        shippingWindow: '',
        estimationData: null,
        estimationResponse: null,
        overlayFlowStatus: overlayFlowStatus?.STS || 'empty',
      }
    }
    return {
      selectedSfsdAddress: {
        addressString: window.localStorage.getItem(STS_ADDRESS_STRING_STORAGE_KEY) || '',
        zip: sfsData.zip?.[0] || '',
        state: sfsData.state?.[0] || '',
        street1: sfsData.street1?.[0] || '',
        latitude: sfsData.latitude?.[0] ? Number(sfsData.latitude[0]) : null,
        longitude: sfsData.longitude?.[0] ? Number(sfsData.longitude[0]) : null,
        city: sfsData.city?.[0] || ''
      },
      shippingWindow: window.localStorage.getItem(STS_SHIPPING_WINDOW_ID) || '',
      estimationData: {
        deliveryEstimations: sfsData.deliveryEstimations,
        physicalStoreId: sfsData.physicalStoreId,
        physicalStoreZipcode: sfsData.physicalStoreZipcode,
        orderType: sfsData.orderType,
        pickUpReadyDate: sfsData.pickUpReadyDate
      },
      estimationResponse: null,
      overlayFlowStatus: overlayFlowStatus?.STS || 'empty'
    }
  } catch (error) {
    console.error('Error initializing STS state:', error)
    return {
      selectedSfsdAddress: null,
      shippingWindow: '',
      estimationData: null,
      overlayFlowStatus: null
    }
  }
}

export const getInitialCEPState = () => {
  try {
    const overlayFlowStatus = safeJSONParse(window.localStorage.getItem(SHIPPING_OVERLAY_FLOW) || '{}')
    return {
      availableSlots: [],
      zipForShipping: window.localStorage.getItem(CEP_ZIP_STORAGE_KEY) || '',
      selectedDate: window.localStorage.getItem(CEP_DATE_STORAGE_KEY) || '',
      selectedSlot: window.localStorage.getItem(CEP_SLOT_STORAGE_KEY) || '',
      selectedDateExpired:false,
      zipCodeShippingOptions: null,
      overlayFlowStatus: overlayFlowStatus?.CEP || 'empty',
      estimatedDeliveryDate: null
    }
  } catch (error) {
    console.error('Error while initializing CEP state',error)
    return {
      availableSlots: [],
      zipForShipping: '',
      selectedDate: '',
      selectedSlot: '',
      selectedDateExpired: false,
      zipCodeShippingOptions: null,
      overlayFlowStatus: null,
      estimatedDeliveryDate: null
    }
  }
}

export const getInitialTollState = () => {
  try {
    const overlayFlowStatus = safeJSONParse(window.localStorage.getItem(SHIPPING_OVERLAY_FLOW) || '{}')
    const tollShippingAddress:FormattedAddress = safeJSONParse(
      window.localStorage.getItem(TOLL_DATA_FULL_ADDRESS_KEY) || '{}'
    )
    return {
      selectedAddress: isEmpty(tollShippingAddress) ? {
        addressString: window.localStorage.getItem(TOLL_SHIPPING_ADDRESS_KEY) || '',
      } : {
        addressString: window.localStorage.getItem(TOLL_SHIPPING_ADDRESS_KEY) || '',
        ...tollShippingAddress
      },
      tollData: safeJSONParse(window.localStorage.getItem(TOLL_DATA_ADDRESS_KEY) || '{}'),
      selectedStore: null,
      apiStatus: {
        loading: false,
        success: false,
        error: null
      },
      overlayFlowStatus: overlayFlowStatus?.TOLL || 'empty'
    }
  } catch (error) {
    console.error('Error initializing TOLL state:', error)
    return {
      selectedAddress: null,
      tollData: null,
      selectedStore: null,
      apiStatus: {
        loading: false,
        success: false,
        error: null
      },
      overlayFlowStatus: null
    }
  }
}

/*
* remove the shipping info keys from localStorage
* @param keys - array of keys to remove
* */
export const removeInfoKeys = (keys:string[]) => {
  try {
      keys.forEach(key => {
        localStorage.removeItem(key);
      });
  } catch (e) {
    console.error('Error removing shipping info keys:', e);
  }
}

export const getCartUrl = (): string => {
  const { storeId, catalogId, langId } = wcs.getStoreConfig()
  return `/OrderCalculate?updatePrices=1&calculationUsageId=-1&errorViewName=AjaxOrderItemDisplayView&catalogId=${catalogId}&langId=${langId}&URL=AjaxOrderItemDisplayView&storeId=${storeId}&orderId=.`
}

export const handleCartChangeOnLogin = async (): Promise<boolean> => {
  const actualCount = parseInt(localStorage.getItem(SGH_CART_ITEMS_KEY) || '0') || 0
  const updatedCount = await getNumberitem()
  if (actualCount !== updatedCount) {
    Object.values(ShippingMethodsKeys).forEach(keys => removeInfoKeys(keys))
    if (wcs.isDelivery()) {
      window.location.href = getCartUrl()
      return true
    }
  }
  return false
}

export const mapOrderItemsToPartNumberQuantity = (orderItemsData: Record<string, string> = {}): Record<string, string> | undefined => {
  try {
    return Object.keys(orderItemsData)?.reduce((acc: Record<string, string>, item: any) => {
      const quantity = parseFloat(orderItemsData[item]).toFixed(1);
      acc[item.partNumber] = quantity.endsWith('.0') ? quantity : quantity;
      return acc;
    }, {});
  } catch (e) {
    console.error('Error mapping order items to object:', e);
    return undefined;
  }
}

export const isOrderStella = (order?: Order):boolean => {
  try {
    return !!order?.orderItems?.find(orderItem => orderItem?.stellaItem)
  } catch (e) {
    console.error('Error checking if order is stella:', e)
    return false
  }
}

export const isOrderEngraved = (order?: Order):boolean => {
  try {
    return !!order?.orderItems?.find(orderItem => orderItem.engraved === 'true')
  } catch (e) {
    console.error('Error checking if order is engraved:', e)
    return false
  }
}

export const isOrderGiftWrapped = (order?: Order):boolean => {
  try {
    return !!order?.hasGiftWrap

  } catch (e) {
    console.error('Error checking if order is gift wrapped:', e)
    return false
  }
}

export const isOrderRx = (order?: Order):boolean => {
  try {
    return !!order?.orderItems.some(item => item.rxItem)
  } catch (e) {
    console.error('Error checking if order is rx:', e)
    return false
  }
}

export const isOrderRemix = (order?: Order):boolean => {
  try {
    return !!order?.orderItems.some(item => item.remixed)
  } catch (e) {
    console.error('Error checking if order is remix:', e)
    return false
  }
}


export const isOrderMetaRx = (order?: Order):boolean => {
  try {
    return !!order?.orderItems.some(item => item.metaRxItem)
  } catch (e) {
    console.error('Error checking if order is meta rx:', e)
    return false
  }
}

export const isOrderMixAndMatch = (order?: Order):boolean => {
  try {
    return !!order?.orderItems.some(item => item["m&mItem"])
  } catch (e) {
    console.error('Error checking if order is mix and match:', e)
    return false
  }
}
/*
* check if the order quantity has changed
* @param oldVal - the old order items data
* @param newVal - the new order items data
* @returns true if the order quantity has changed, false otherwise
* */
export const getUpdateOrderItemsData = (
  oldVal: {
    orderItemsData: { [key: string]: string } | undefined
    orderItems: OrderItem[] | null | undefined
  },
  newVal?: {
    orderItemsData: { [key: string]: string } | undefined
    orderItems: OrderItem[] | null | undefined
  }
): {
  newItemsAdded: boolean
  itemRemoved: boolean
  quantityChanged: boolean
  prevItems?: { [key: string]: string }
  newItems?: { [key: string]: string }
  oldQuantity?: number
  newQuantity?: number
  lastAddedItem?: OrderItem | null
  removedItem?: OrderItem | null
  quantityChangedItem?: OrderItem | null
} => {
  try {
    // If new items were added (newVal has more keys than oldVal)
    const newData = newVal?.orderItemsData || {}
    const oldData = oldVal?.orderItemsData || {}
    const newOrderItems = newVal?.orderItems || []
    const oldOrderItems = oldVal?.orderItems || []
    const newItemIsAdded = (newData && oldData && Object.keys(newData).length > Object.keys(oldData).length) || newOrderItems?.length > oldOrderItems ?.length || 0
    if (newItemIsAdded) {
      const lastAddedKey = Object.keys(newData).find(key => !(key in oldData))
      return {
        newItemsAdded: true,
        quantityChanged: false,
        itemRemoved:false,
        prevItems: oldData,
        newItems: newData,
        lastAddedItem: newVal?.orderItems?.find(item => item.partNumber === lastAddedKey),
      }
    }
    // If old items were removed (oldVal has more keys than newVal)
    if (newData && oldData && Object.keys(oldData).length > Object.keys(newData).length) {
      const removedKey = Object.keys(oldData).find(key => !(key in newData))
      return {
        newItemsAdded: false,
        quantityChanged: false,
        itemRemoved:true,
        prevItems: oldData,
        newItems: newData,
        removedItem: oldVal?.orderItems?.find(item => item.partNumber === removedKey),
      }
    }
    // Check for quantity changes in existing items
    const allItemKeys = new Set([...Object.keys(oldData || {}), ...Object.keys(newData || {})])

    for (const itemKey of allItemKeys) {
      const oldQuantity = oldData?.[itemKey] ? parseInt(oldData[itemKey]) : 0
      const newQuantity = newData?.[itemKey] ? parseInt(newData[itemKey]) : 0

      if (newQuantity !== oldQuantity) {
        return {
          newItemsAdded: false,
          quantityChanged: true,
          itemRemoved:false,
          oldQuantity,
          newQuantity,
          quantityChangedItem: newVal?.orderItems?.find(item => item.partNumber === itemKey),
        }
      }
    }
    return {
      newItemsAdded: false,
      quantityChanged: false,
      itemRemoved:false,
      prevItems: oldData,
      newItems: newData,
      lastAddedItem: null,
      quantityChangedItem: null,
    }
  } catch (e) {
    console.error('Error checking if order quantity changed:', e)
    return {
      newItemsAdded: false,
      itemRemoved:false,
      quantityChanged: false,
      prevItems: oldVal?.orderItemsData,
      newItems: newVal?.orderItemsData,
      lastAddedItem: null,
      quantityChangedItem: null
    }
  }
}
/*
* update the overlay flow status in localStorage
* @param status - the status to set
* @param shippingMethodCode - the shipping method code
* */
export const updateOverLayFlowStateStorageKey = ({
  status,
  shippingMethodCode,
}: {
  status: OverlayFlowStatus
  shippingMethodCode: string
}) => {
  try {
    const shippingOverlayFlow =
      safeJSONParse(window.localStorage.getItem(SHIPPING_OVERLAY_FLOW) || '{}') || null
    shippingOverlayFlow[shippingMethodCode] = status
    window.localStorage.setItem(SHIPPING_OVERLAY_FLOW, JSON.stringify(shippingOverlayFlow))
  } catch (e) {
    console.error('Error setting overlay flow state:', e)
  }
}

export const hasUnavailableOrderItems = (orderItems: any[]): boolean => {
  return Array.isArray(orderItems) && orderItems.some(
    (item: { isUnavailable: boolean }) => item.isUnavailable
  )
}

export const syncExpressButtonsVisibility = (cartState: any) => {
  if (!cartState) return
  const selectedId = cartState.shippingMethodsData?.selectedShippingId

  const allMethods = cartState.shippingMethodsData?.shippingMethods || []
  const selectedMethod = allMethods.find((m: any) => m.id === selectedId)

  if (!selectedMethod) return;

  const isCnc = selectedMethod.type === 'cnc'
  const isSts = isOneDayShipping(selectedMethod.type)
  const isToll = isTollShipping(selectedMethod.type)
  const hasUnavailableItems = hasUnavailableOrderItems(cartState?.orderItems)

  if (isCnc || isSts || isToll || hasUnavailableItems) {
    const selectors = [
      '.paypalbutton__link',
      '#afterpay-express-button',
      '[id^="klarna-express-button"]',
      '#applePayButton'
    ]

    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        el.classList.add('hide')
      })
    })

    window?.showHidePolicyText()
  }
}


/**
 * Evaluates what UI should do based on API errors + omniStatus flags.
 *
 * Rules implemented:
 * 1) NOT_ELIGIBLE_PRODUCTS => eligibility error, no input (global, independent of omni)
 * 2) CNC/STS:
 *    - If NOT_ELIGIBLE_PRODUCTS_CNC present AND cnc status true:
 *        - if sts status false => eligibility error (no input)
 *        - if sts status true  => allow input (no error)
 *    - If NOT_ELIGIBLE_PRODUCTS_STS present AND sts status true:
 *        - if cnc status false => eligibility error (no input)
 *        - if cnc status true  => allow input (no error)
 *    - If BOTH CNC+STS errors present while both statuses true => eligibility error (no input)
 * 3) SFS:
 *    - If NOT_ELIGIBLE_PRODUCTS_SFS present:
 *        - if sfs status false => no error
 *        - if sfs status true  => eligibility error
 */
export const evaluateOmniUiState = (apiResponse: ShippingMethodsResponse):{
  showInput: boolean,
  showEligibilityError: boolean,
  eligibilityReason: string | null
  ui: string | null,
} => {
  try {
    const { selectedShippingId, cncFeatureStatus, stsFeatureStatus, sfsFeatureStatus } =
      apiResponse || {}
    const selectedShippingMethodData = apiResponse.shippingMethods?.find(
      method => method.id === selectedShippingId
    )
    const { error: shippingMethodErrors } = selectedShippingMethodData || {}
    const hasGlobal = shippingMethodErrors?.includes(omniChannelErrors.NOT_ELIGIBLE_PRODUCTS)
    const hasSfsErr = shippingMethodErrors?.includes(omniChannelErrors.NOT_ELIGIBLE_PRODUCTS_SFS)
    const hasCncErr = shippingMethodErrors?.includes(omniChannelErrors.NOT_ELIGIBLE_PRODUCTS_CNC)
    const hasStsErr = shippingMethodErrors?.includes(omniChannelErrors.NOT_ELIGIBLE_PRODUCTS_STS)

    // ---- 1) Global error overrides everything ----
    if (hasGlobal) {
      return {
        showInput: false,
        showEligibilityError: true,
        eligibilityReason: 'GLOBAL',
        ui: 'Eligibility error message',
      }
    }

    // ---- 2) SFS rule (only show error if status=true) ----
    if (hasSfsErr && sfsFeatureStatus) {
      return {
        showInput: false,
        showEligibilityError: true,
        eligibilityReason: 'SFS',
        ui: 'Eligibility error message',
      }
    }
    // if (hasSfsErr && !sfs) => explicitly no error from SFS; continue evaluating CNC/STS

    // ---- 3) CNC/STS rules (only consider CNC/STS errors when corresponding status=true) ----
    const cncErrActive = hasCncErr && cncFeatureStatus
    const stsErrActive = hasStsErr && stsFeatureStatus

    // Both errors present while both statuses true => eligibility error, no input
    if (cncErrActive && stsErrActive) {
      return {
        showInput: false,
        showEligibilityError: true,
        eligibilityReason: 'CNC+STS',
        ui: 'Eligibility error message',
      }
    }

    // If CNC error is active and STS not available => error
    if (cncErrActive && !stsFeatureStatus) {
      return {
        showInput: false,
        showEligibilityError: true,
        eligibilityReason: 'CNC',
        ui: 'Eligibility error message',
      }
    }

    // If STS error is active and CNC not available => error
    if (stsErrActive && !cncFeatureStatus) {
      return {
        showInput: false,
        showEligibilityError: true,
        eligibilityReason: 'STS',
        ui: 'Eligibility error message',
      }
    }

    // If we reach here:
    // - Either no active CNC/STS errors, OR an active error is "covered" by the other service being available
    const hasAnyOmni = cncFeatureStatus || stsFeatureStatus

    if (hasAnyOmni) {
      return {
        showInput: true,
        showEligibilityError: false,
        eligibilityReason: null,
        ui: 'Show input field (no eligibility error)',
      }
    }

    return {
      showInput: false,
      showEligibilityError: false,
      eligibilityReason: null,
      ui: 'No omni input (CNC/STS not available)',
    }
  } catch (e) {
    console.error('error while evaluating omnichannel ui state',e)
    return {
      showInput: false,
      showEligibilityError: false,
      eligibilityReason: null,
      ui: null,
    }
  }
}

/**
 * Sorts and orders the CNC stores according to stores availability
 */
export const formatAndOrderStoresList = ({
  storeDetailsResponse,
  orderItemsData,
  currentlySelectedStore,
  is12HourClockCountry,
  cncWeekDaysNames,
}: {
  is12HourClockCountry: boolean
  cncWeekDaysNames: string[]
  orderItemsData?: Record<string, string>
  storeDetailsResponse?: StoreDetails | null
  currentlySelectedStore?:  SelectedStore | null
}): {formattedStoresList:FormattedStore[] | null,sortedList: (FormattedStore | LocationDetail)[] | null} | null=> {
  try {
    const sortedList = storeDetailsResponse ? sortStores(storeDetailsResponse, orderItemsData || {}) : null
    //if a preSelectedStore exists (from a pdp with cnc we put it on the top of the list)
    const sortedListByPriority = !!currentlySelectedStore?.physicalStoreId && sortedList
      ? prioritizeStoreById(sortedList, currentlySelectedStore?.physicalStoreId)
      : sortedList
    const formattedStoresList = formatStoresList(
      sortedListByPriority,
      cncWeekDaysNames,
      orderItemsData,
      is12HourClockCountry
    )
    return  {
      formattedStoresList,
      sortedList
    }
  } catch (e) {
    console.error('error while reordering physical stores list', e)
    return null
  }
}


